import { StyleSheet } from "react-native";

import {
  fonts,
  palette,
  radii,
  shadows,
  spacing,
  type ThemeColors,
} from "~/styles/theme";

export const sidebarInset = spacing.m;

export const makeStylesheet = (colors: ThemeColors) =>
  StyleSheet.create({
    appShell: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: colors.background,
    },
    appShellContent: { flex: 1, minWidth: 0 },
    expiredBanner: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.m,
      paddingVertical: spacing.s,
      backgroundColor: colors.danger,
    },
    sidebar: {
      width: 240,
      padding: 20,
      backgroundColor: palette.brandForest,
    },
    sidebarBody: { flex: 1, gap: spacing.s },
    sidebarBrand: {
      marginBottom: 20,
    },
    workspaceTrigger: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
      marginBottom: spacing.m,
      paddingHorizontal: spacing.s,
      paddingVertical: spacing.s,
      borderWidth: 1,
      borderColor: "rgba(255, 255, 255, 0.22)",
      borderRadius: radii.m,
    },
    workspaceIcon: {
      width: 30,
      height: 30,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: radii.s,
      backgroundColor: palette.brandMint,
    },
    workspaceTriggerCopy: { flex: 1, minWidth: 0 },
    workspaceName: {
      color: colors.background,
      fontFamily: fonts.semibold,
      fontSize: 13,
    },
    workspaceHint: {
      color: "rgba(255, 255, 255, 0.62)",
      fontFamily: fonts.regular,
      fontSize: 11,
    },
    sidebarDivider: {
      paddingTop: spacing.l,
      marginTop: spacing.l,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: "rgba(255, 255, 255, 0.28)",
    },
    navItem: {
      minHeight: 42,
      justifyContent: "center",
      paddingHorizontal: sidebarInset,
      borderLeftWidth: 3,
      borderLeftColor: "transparent",
      borderRadius: radii.s,
    },
    navItemActive: {
      backgroundColor: "rgba(255, 255, 255, 0.07)",
      borderLeftColor: palette.brandMint,
    },
    navItemHover: { backgroundColor: "rgba(255, 255, 255, 0.12)" },
    navItemText: {
      color: "rgba(255, 255, 255, 0.72)",
      fontFamily: fonts.medium,
      fontSize: 14,
    },
    navItemTextActive: {
      color: colors.background,
      fontFamily: fonts.semibold,
    },
    navSectionLabel: {
      marginTop: spacing.s,
      paddingHorizontal: sidebarInset,
      color: "rgba(255, 255, 255, 0.48)",
      fontFamily: fonts.semibold,
      fontSize: 10,
      letterSpacing: 0.8,
      textTransform: "uppercase",
    },
    accountNavList: { gap: 2, paddingBottom: spacing.xs },
    accountNavItem: {
      minHeight: 32,
      justifyContent: "center",
      marginLeft: sidebarInset,
      paddingHorizontal: sidebarInset,
      borderRadius: radii.s,
    },
    accountNavItemActive: { backgroundColor: "rgba(255, 255, 255, 0.08)" },
    accountNavItemLocked: { opacity: 0.45 },
    accountNavText: {
      color: "rgba(255, 255, 255, 0.64)",
      fontFamily: fonts.regular,
      fontSize: 12,
    },
    accountNavTextActive: {
      color: colors.background,
      fontFamily: fonts.semibold,
    },
    accountNavTextLocked: {
      textDecorationLine: "line-through",
    },
    accountSectionList: {
      gap: 2,
      marginLeft: sidebarInset + spacing.s,
      marginBottom: spacing.xs,
    },
    accountSectionItem: {
      minHeight: 28,
      justifyContent: "center",
      paddingHorizontal: spacing.s,
      borderLeftWidth: 2,
      borderLeftColor: "transparent",
    },
    accountSectionItemActive: { borderLeftColor: palette.brandMint },
    accountSectionText: {
      color: "rgba(255, 255, 255, 0.6)",
      fontFamily: fonts.regular,
      fontSize: 11,
    },
    accountSectionTextActive: {
      color: colors.background,
      fontFamily: fonts.semibold,
    },
    sidebarUtilityLink: {
      minHeight: 34,
      justifyContent: "center",
      paddingHorizontal: sidebarInset,
      paddingVertical: 6,
      borderLeftWidth: 3,
      borderLeftColor: "transparent",
      borderRadius: radii.s,
    },
    sidebarUtilityText: {
      color: "rgba(255, 255, 255, 0.82)",
      fontFamily: fonts.regular,
      fontSize: 14,
    },
    utilityContent: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
    },
    notificationBadge: {
      minWidth: 18,
      height: 18,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 5,
      borderRadius: 9,
      backgroundColor: palette.brandMint,
    },
    notificationBadgeText: {
      color: palette.brandDark,
      fontFamily: fonts.bold,
      fontSize: 10,
    },
    sidebarFooter: {
      gap: spacing.s,
      paddingTop: spacing.l,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: "rgba(255, 255, 255, 0.28)",
    },
    sidebarProfile: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      borderRadius: radii.m,
    },
    sidebarAvatar: {
      width: 34,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 17,
      backgroundColor: palette.brandMint,
    },
    sidebarAvatarText: {
      color: palette.brandDark,
      fontFamily: fonts.bold,
      fontSize: 12,
    },
    sidebarProfileName: {
      flex: 1,
      color: "rgba(255, 255, 255, 0.88)",
      fontFamily: fonts.medium,
      fontSize: 13,
    },
    shellHeader: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
      paddingHorizontal: spacing.l,
      borderBottomWidth: 1,
      borderBottomColor: colors.hairline,
      backgroundColor: colors.background,
    },
    shellHeaderLeft: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    shellActions: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.s,
    },
    timerQuickAction: {
      minHeight: 34,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
      paddingHorizontal: spacing.s,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.pill,
      backgroundColor: colors.mutedSurface,
    },
    timerQuickActionText: {
      color: colors.heading,
      fontFamily: fonts.semibold,
      fontSize: 12,
    },
    headerIconButton: {
      width: 34,
      height: 34,
      alignItems: "center",
      justifyContent: "center",
      borderWidth: 1,
      borderColor: colors.hairline,
      borderRadius: radii.pill,
      backgroundColor: colors.background,
    },
    headerIconButtonHover: { backgroundColor: colors.surfaceSunken },
    headerBadge: {
      position: "absolute",
      top: 7,
      right: 7,
      width: 7,
      height: 7,
      borderRadius: 4,
      backgroundColor: colors.danger,
    },
    breadcrumbs: {
      flex: 1,
      minWidth: 0,
      flexDirection: "row",
      alignItems: "center",
    },
    breadcrumbItem: { flexDirection: "row", alignItems: "center" },
    breadcrumbLink: {
      color: colors.mutedForeground,
      fontFamily: fonts.regular,
      fontSize: 13,
    },
    breadcrumbCurrent: {
      flexShrink: 1,
      color: colors.heading,
      fontFamily: fonts.semibold,
      fontSize: 13,
    },
    breadcrumbSeparator: {
      color: colors.mutedForeground,
      fontFamily: fonts.regular,
      fontSize: 12,
      marginHorizontal: spacing.xs,
    },
    drawerOverlay: {
      flex: 1,
      flexDirection: "row",
      backgroundColor: "rgba(9, 50, 74, 0.35)",
    },
    drawerPanel: {
      width: "82%",
      maxWidth: 320,
      padding: 20,
      backgroundColor: palette.brandForest,
    },
    drawerDismiss: { flex: 1 },
    modalOverlay: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
      backgroundColor: colors.overlay,
    },
    confirmationCard: {
      width: "100%",
      maxWidth: 420,
      padding: 20,
      borderRadius: radii.l,
      backgroundColor: colors.background,
      gap: spacing.l,
      ...shadows.md,
    },
    workspacePickerCard: {
      width: "100%",
      maxWidth: 520,
      padding: 20,
      borderRadius: radii.l,
      backgroundColor: colors.background,
      gap: spacing.l,
      ...shadows.md,
    },
    workspacePickerHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: spacing.m,
    },
    workspacePickerHeading: { flex: 1, minWidth: 0, gap: spacing.xs },
    workspaceOptions: { gap: spacing.s },
    workspaceOption: {
      minHeight: 64,
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    workspaceOptionSelected: {
      borderColor: palette.brandForest,
      backgroundColor: colors.surface,
    },
    workspaceOptionHover: { backgroundColor: colors.mutedSurface },
    workspaceOptionIcon: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: radii.s,
      backgroundColor: colors.mutedSurface,
    },
    workspaceOptionCopy: { flex: 1, minWidth: 0 },
    workspaceOptionName: {
      color: colors.heading,
      fontFamily: fonts.semibold,
      fontSize: 14,
    },
    modalTitle: {
      color: colors.heading,
      fontFamily: fonts.bold,
      fontSize: 22,
    },
    confirmationActions: {
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: spacing.s,
    },
  });
